/**
 * SentPage — 发件箱（已发送）
 * 账号级发信记录（mail_send_logs）列表：关键词搜索 + 分页 + 只读详情 + 写邮件入口。
 * 复用 DomainSendLogsTab 的表格与详情交互，区别是不按收件域过滤，展示全部发送记录。
 */
import { useMemo, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Field,
  InfoLabel,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components'
import { SearchRegular, DismissRegular, ArrowSyncRegular, MailAdd24Regular } from '@fluentui/react-icons'
import { sendLogsApi } from '@/api/sendlogs'
import type { SendLog } from '@/api/types'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatDate } from '@/lib/format'
import ComposeModal from '@/pages/ComposeModal'

const PAGE_SIZE = 10

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  queryCard: { padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  formRow: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' },
  keywordField: { flex: '2 0 240px', minWidth: '220px', flexShrink: 0 },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
  },
  tableCard: { padding: '16px' },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
})

const STATUS_LABEL: Record<string, string> = {
  queued: '已入队',
  sent: '已发送',
  failed: '失败',
}

export default function SentPage() {
  const styles = useStyles()
  const [reloadKey, setReloadKey] = useState(0)
  const [detailLog, setDetailLog] = useState<SendLog | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeInit, setComposeInit] = useState<{
    from: string
    to: string
    subject: string
    body: string
    title: string
  }>({ from: '', to: '', subject: '', body: '', title: '写邮件' })

  const { data: logs, isLoading } = useQuery({
    queryKey: ['send-logs', reloadKey],
    queryFn: () => sendLogsApi.list(),
  })

  const [draftQ, setDraftQ] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [query])

  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const list = logs ?? []
    if (!q) return list
    return list.filter(
      (r) =>
        (r.subject ?? '').toLowerCase().includes(q) ||
        r.recipient.toLowerCase().includes(q) ||
        (r.sender ?? '').toLowerCase().includes(q) ||
        (r.client_name ?? '').toLowerCase().includes(q) ||
        (r.status ?? '').toLowerCase().includes(q),
    )
  }, [logs, q])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const resetQuery = () => {
    setDraftQ('')
    setQuery('')
    setPage(1)
  }
  const applyQuery = () => {
    setQuery(draftQ)
    setPage(1)
  }

  const columns: PagedColumn<SendLog>[] = [
    {
      key: 'subject',
      header: '主题',
      width: '240px',
      render: (r) => <Text weight="semibold">{r.subject || r.recipient}</Text>,
    },
    {
      key: 'sender',
      header: '发件人',
      width: '200px',
      render: (r) => r.sender ?? '-',
    },
    {
      key: 'recipient',
      header: '收件人',
      width: '200px',
      render: (r) => r.recipient,
    },
    {
      key: 'client_name',
      header: '来源应用',
      width: '150px',
      render: (r) => r.client_name ?? '-',
    },
    {
      key: 'status',
      header: '状态',
      width: '100px',
      render: (r) => STATUS_LABEL[r.status] ?? r.status ?? '-',
    },
    {
      key: 'created_at',
      header: '时间',
      width: '150px',
      render: (r) => formatDate(r.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>发件箱</MessageBarTitle>
          展示本系统以账号身份发出的邮件记录（含本地投递与外域投递）。点击「写邮件」可发起新发信。
        </MessageBarBody>
      </MessageBar>

      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <Field
            className={styles.keywordField}
            label={<InfoLabel info="按主题、发件人、收件人、来源应用或状态模糊搜索，留空显示全部。">关键词</InfoLabel>}
          >
            <Input
              contentBefore={<SearchRegular />}
              contentAfter={
                draftQ ? (
                  <Button appearance="transparent" size="small" icon={<DismissRegular />} aria-label="清空" onClick={() => setDraftQ('')} />
                ) : undefined
              }
              placeholder="搜索主题 / 发件人 / 收件人…"
              value={draftQ}
              onChange={(_, d) => setDraftQ(d.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyQuery() }}
            />
          </Field>
        </div>
        {query.trim() && (
          <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 }}>
            已应用过滤：关键词 “{query.trim()}”
          </div>
        )}
        <div className={styles.actionsRow}>
          <Button appearance="subtle" onClick={resetQuery}>重置</Button>
          <Button appearance="primary" onClick={applyQuery}>查询</Button>
          <Button appearance="subtle" icon={<ArrowSyncRegular />} onClick={() => setReloadKey((k) => k + 1)} disabled={isLoading}>
            {isLoading ? <Spinner size="tiny" /> : '刷新'}
          </Button>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        <PagedGrid<SendLog>
          rows={pageItems}
          columns={columns}
          loading={isLoading}
          rowKey={(r) => String(r.id)}
          total={filtered.length}
          page={safePage}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyText="暂无发送记录"
          toolbarLeft={
            <Button
              appearance="primary"
              icon={<MailAdd24Regular />}
              onClick={() => {
                setComposeInit({ from: '', to: '', subject: '', body: '', title: '写邮件' })
                setComposeOpen(true)
              }}
            >
              写邮件
            </Button>
          }
          onRowClick={(r) => setDetailLog(r)}
        />
      </Card>

      <Dialog open={detailLog !== null} onOpenChange={(_, d) => !d.open && setDetailLog(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="发送日志详情" onClose={() => setDetailLog(null)} />
            <DialogContent>
              {detailLog && (
                <DetailGrid key={detailLog.id}>
                  <DetailField label="ID" value={detailLog.id} mono />
                  <DetailField label="主题" value={detailLog.subject ?? '-'} />
                  <DetailField label="发件人" value={detailLog.sender ?? '-'} mono />
                  <DetailField label="收件人" value={detailLog.recipient} mono />
                  <DetailField label="来源应用" value={detailLog.client_name ?? '-'} help="来源应用的名称。" />
                  <DetailField label="状态" value={STATUS_LABEL[detailLog.status] ?? detailLog.status ?? '-'} help="发送处理结果。" />
                  <DetailField label="错误信息" value={detailLog.error ?? '-'} colSpan />
                  <DetailField label="时间" value={formatDate(detailLog.created_at)} />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        defaultFrom={composeInit.from}
        defaultTo={composeInit.to}
        defaultSubject={composeInit.subject}
        defaultBody={composeInit.body}
        title={composeInit.title}
      />
    </div>
  )
}
